// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * How a form template's field is named, and how a saved answer to it reads —
 * the one answer for every surface that shows a form instance.
 *
 * Sprint 62.45.0 AC9 wrote these three questions for the printed form
 * (`services/export/forms/field-answer.ts`, which now re-exports this module
 * with the platform's own yes / no). Sprint 63.37.0 Stream J moved them here:
 * the rehearsal found the form instance page answering them on its own, and
 * wrongly. It labelled every answer with its `field_id` turned into English
 * words ("Applicant Name Vi", "Dp Head Name") and printed a choice as its
 * stored value (`yes`, `birth_name, date_of_birth`) under a Vietnamese install,
 * because no instance carries a `template_snapshot` and the page had no other
 * source of labels. The page and the printed form now read the same rules.
 *
 * - **The key.** Every pack-installed field is keyed `field_id`; the
 *   hand-built shape uses `field_key` or `key`.
 * - **The label.** Pack labels are `{ en, vi }` maps, read through
 *   `extractLocale` (exact locale → language prefix → `en` → first value).
 * - **The answer.** A choice field stores its option's `value`; the reader
 *   sees the option's label in the locale.
 */
import { extractLocale } from './locale-extraction.js';
/**
 * The key a field's answer is stored under in `field_values`: `field_id`, then
 * `field_key`, then `key`. `''` when the field declares none.
 */
export function templateFieldKey(field) {
    for (const candidate of [field.field_id, field.field_key, field.key]) {
        if (typeof candidate === 'string' && candidate.length > 0)
            return candidate;
    }
    return '';
}
/** The field's label in `locale`, falling back to its key when it has none. */
export function templateFieldLabel(field, locale) {
    return extractLocale(field.label, locale) || templateFieldKey(field);
}
function optionValue(option) {
    return typeof option === 'string' ? option : option?.value;
}
/**
 * The label of the option whose value is `value`, in `locale`; `null` when the
 * field declares no such option or the option has no label (the caller then
 * writes the value as saved — never an empty cell for an answer that exists).
 */
function optionLabel(field, value, locale) {
    const option = (field.options ?? []).find((o) => optionValue(o) === value);
    if (option === undefined)
        return null;
    if (typeof option === 'string')
        return option;
    const label = extractLocale(option.label, locale);
    return label.length > 0 ? label : null;
}
/** An uploaded attachment reference (`AttachmentRef`) reads as its file name. */
function attachmentName(value) {
    return 'file_id' in value && typeof value.filename === 'string' && value.filename.length > 0
        ? value.filename
        : null;
}
function scalarText(field, value, locale, words) {
    if (value === null || value === undefined)
        return '';
    const label = optionLabel(field, value, locale);
    if (label !== null)
        return label;
    // The checkbox control stores a boolean: the platform's own yes / no, in
    // the reader's language, rather than the literal `true`.
    if (typeof value === 'boolean')
        return value ? words.yes : words.no;
    if (typeof value === 'object') {
        return attachmentName(value) ?? JSON.stringify(value);
    }
    // A `date` answer (`YYYY-MM-DD`) and a `datetime` answer, which the
    // datetime-local control stores as LOCAL wall time `YYYY-MM-DDTHH:mm` with no
    // offset, read as `dd/MM/yyyy` and `dd/MM/yyyy HH:mm` — rearranged as text,
    // never parsed through `Date`, which would attach a timezone the operator
    // never stated (Sprint 63.37.0, Stream J: the rehearsal read
    // "2026-10-18T22:00" on a Vietnamese Mẫu số 08). Any other string is written
    // as saved.
    const text = String(value);
    const kind = field.field_type ?? field.type;
    if (kind === 'date' || kind === 'datetime') {
        const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(text);
        if (m)
            return `${m[3]}/${m[2]}/${m[1]}${m[4] !== undefined ? ` ${m[4]}:${m[5]}` : ''}`;
    }
    return text;
}
/**
 * The text a reader sees for a saved answer: an option's label in `locale`
 * for a choice field (each selected option for a multi-value answer, joined),
 * `words.yes` / `words.no` for a boolean, a file name for an attachment, the
 * value as saved otherwise, and `''` for no answer.
 */
export function formatFormFieldAnswer(field, value, locale, words) {
    if (Array.isArray(value)) {
        return value
            .map((item) => scalarText(field, item, locale, words))
            .filter((s) => s.length > 0)
            .join(', ');
    }
    return scalarText(field, value, locale, words);
}
//# sourceMappingURL=form-field-answer.js.map